// VideoFrameScrubber.tsx
import { useRef, useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid2,
  Slider,
  SliderThumb,
  IconButton,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
} from "@mui/material";
import UCVideoSlider from "./UCVideoSlider";
import DraggableVideoWrapper from "./DraggableVideo";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
// @ts-ignore
import CopyIframeUrlButton from "./CopyIframeUrlButton";
import SyncedVideo from "./SyncedVideo";
import { SLIDER_PRECISION, useVideoPlayerState } from "../../hooks/video/useVideoPlayerState";
import { useVideoControls } from "../../hooks/video/useVideoControls";
import { useCarouselContext } from "../../context/CarouselContext";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useThemeContext } from "../../context/ThemeContext";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";

type UcLayout = 'side-by-side' | 'vertical' | 'left' | 'right';

const VideoFrameScrubber: React.FC = () => {
  const {
    filteredImages,
    ucTime,
    ucImageSrc,
    ucAudioSrc,
    inModal,
    setUcImageSrc,
    setUcTime,
    setInModal,
    setIsUcViewerActive
  } = useCarouselContext();

  const theme = useTheme();
  const { darkMode } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [ucLayout, setUcLayout] = useState<UcLayout>(isMobile ? 'vertical' : 'side-by-side');

  useEffect(() => {
    setUcLayout(isMobile ? 'vertical' : 'side-by-side');
  }, [isMobile]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLayoutChange = (layout: UcLayout) => {
    setUcLayout(layout);
    handleMenuClose();
  };

  const videoUrls = useMemo(() => {
    if (inModal) {
      return ucImageSrc ? [ucImageSrc] : [];
    }

    return filteredImages
      .filter(img => img.imageType?.includes("uc"))
      .sort((a, b) => (a.src.toLowerCase().includes("right") ? -1 : 1))
      .map(img => img.src);

  }, [filteredImages, inModal, ucImageSrc]);

  const hasMultipleVideos = videoUrls.length > 1;

  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const singleVideoRef = useRef<HTMLVideoElement | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  const audioSrc = ucAudioSrc || ''

  const playerState = useVideoPlayerState({
    initialTime: ucTime,
    videoRefs,
    singleVideoRef,
    hasMultipleVideos,
  });

  const {
    duration,
    currentTime,
    sliderValue,
    isPlaying,
    isLoaded,
    setDuration,
    setIsLoaded,
    setIsPlaying,
  } = playerState;

  const {
    handleVideoTimeUpdate,
    handleSliderDrag,
    handleSliderChangeCommitted,
    captureFrame,
  } = useVideoControls({ playerState });

  const toggleAudio = async () => {
    const a = audioRef.current;
    if (!a) return;

    try {
      if (a.paused) {
        a.currentTime = 0;   // always start from beginning
        await a.play();
        // state will flip via onPlay
      } else {
        a.pause();
        a.currentTime = 0;   // stop + rewind
        // state will flip via onPause
      }
    } catch (e) {
      console.error("Audio play failed:", e);
      setIsAudioPlaying(false);
    }
  };

  const handleFrameClick = (videoUrl: string, time: number) => {
    if (!inModal) {
      setUcTime(time);
      setUcImageSrc(videoUrl);
      setInModal(true);
      setIsUcViewerActive(true);
    }
  };

  function ArrowThumb(props: any) {
    const { children, ...other } = props;

    return (
      <SliderThumb {...other}>
        <SwapHorizIcon
          sx={{
            fontSize: "1.2rem",
            color: "red",
            pointerEvents: "none",
            animation: "wiggle 1.2s ease-in-out 2",
            "@keyframes wiggle": {
              "0%": { transform: "translateX(0)" },
              "25%": { transform: "translateX(-2px)" },
              "50%": { transform: "translateX(2px)" },
              "75%": { transform: "translateX(-1px)" },
              "100%": { transform: "translateX(0)" },
            },
          }}
        />
        {children}
      </SliderThumb>
    );
  }

  const videosToRender = useMemo(() => {
    if (!hasMultipleVideos) return videoUrls;

    const [leftVideo, rightVideo] = videoUrls;

    switch (ucLayout) {
      case 'left':
        return [leftVideo];
      case 'right':
        return [rightVideo];
      case 'vertical':
      case 'side-by-side':
      default:
        return videoUrls;
    }
  }, [videoUrls, ucLayout, hasMultipleVideos]);

  const dynamicAspectRatio = useMemo(() => {
    const singleVideo = videosToRender.length === 1;
    if (singleVideo) {
      return '4 / 3'; // Standard 4:3 for single video
    }
    if (ucLayout === 'vertical') {
      return undefined; // Two 4:3 videos stacked, becomes 2:3 or 4:6
    }
    // Default for side-by-side
    return '8 / 3';
  }, [videosToRender, ucLayout]);

  return (
    <Paper
      elevation={2}
      sx={{ p: 2, width: "100%", height: "100%", display: "flex", flexDirection: "column" }}
    >
      {/* --- Video Display Area (Simplified with pure Flexbox) --- */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {inModal ? (
          // --- Single Video "inModal" View (Draggable) ---
          <Grid2 container spacing={2} sx={{ height: "100%", alignItems: 'center' }}>
            <Grid2 size={2}>
              <UCVideoSlider
                value={sliderValue}
                onChange={handleSliderDrag}
                onChangeCommitted={handleSliderChangeCommitted}
                disabled={isPlaying}
              />
            </Grid2>
            <Grid2
              size={10}
              sx={{
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <DraggableVideoWrapper
                videoRef={singleVideoRef}
                videoUrl={videoUrls[0]}
                currentTime={currentTime}
                // isPlaying={isPlaying}
                onPlayPauseChange={setIsPlaying}
                onEnded={() => {
                  setIsPlaying(false);
                  playerState.setSliderValue(0);
                  playerState.setCurrentTime(0);
                }}
                onLoaded={(duration) => {
                  setDuration(duration);
                  setIsLoaded(true);
                }}
                onTimeUpdate={handleVideoTimeUpdate}
              />
            </Grid2>
          </Grid2>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: ucLayout === 'vertical' ? 'column' : 'row',
              gap: 2,
              width: ucLayout === 'side-by-side' ? '50%' : '100%',
              height: ucLayout === 'side-by-side' ? 'auto' : '100%',
              maxHeight: '100%', // Always constrain to the parent
              maxWidth: '100%', // Always constrain to the parent
              aspectRatio: dynamicAspectRatio,
            }}
          >
            {videosToRender.map((src, i) => (
              <SyncedVideo
                key={src}
                src={src}
                // isPlaying={isPlaying}
                currentTime={currentTime}
                multiVid={false}
                onLoaded={(videoDuration) => {
                  setDuration(videoDuration);
                  setIsLoaded(true);
                }}
                onFrameClick={handleFrameClick}
                onPlayPauseChange={setIsPlaying}
                onTimeUpdate={handleVideoTimeUpdate}
                onEnded={() => {
                  console.log("onEnded Called")
                  setIsPlaying(false);
                  playerState.setCurrentTime(0);
                  playerState.setSliderValue(0);
                }}
                storeRef={(ref) => {
                  const originalIndex = videoUrls.indexOf(src);
                  if (videoRefs.current) videoRefs.current[originalIndex] = ref;
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      <audio
        ref={audioRef}
        src={audioSrc}
        preload="auto"
        onPlay={() => setIsAudioPlaying(true)}
        onPause={() => setIsAudioPlaying(false)}
        onEnded={() => {
          const a = audioRef.current;
          if (a) a.currentTime = 0; // rewind
          setIsAudioPlaying(false);
        }}
        style={{ display: "none" }}
      />

      {/* --- Controls Area --- */}
      <Box>
        {hasMultipleVideos && (
          <Slider
            value={sliderValue}
            onChange={handleSliderDrag}
            onChangeCommitted={handleSliderChangeCommitted}
            min={0}
            max={SLIDER_PRECISION}
            slots={{
              thumb: ArrowThumb, // ✅ component type
            }}
            sx={{
              height: 8,
              width: "100%",
              cursor: "grab",

              "& .MuiSlider-rail": {
                opacity: 0.25,
                backgroundColor: darkMode ? "#fff" : '#000',
              },
              "& .MuiSlider-track": {
                backgroundColor: "red",
              },

              "& .MuiSlider-thumb": {
                width: 40,
                height: 28,
                borderRadius: 8,
                backgroundColor: "white",
                border: "3px solid red",

                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',

                '&:hover': {
                  boxShadow: '0 0 0 8px rgba(255, 0, 0, 0.16)', // Example hover effect
                },

                // ✅ CORRECT: Style the icon within the thumb
                "& .MuiSvgIcon-root": {
                  fontSize: '1.2rem',
                  color: 'red'
                }
              },
            }}
          />
        )}
        {isLoaded && (
          <Box mt={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
            <Typography variant="body2" color="text.secondary" noWrap>
              {`Time: ${currentTime.toFixed(2)}s / ${duration.toFixed(2)}s`}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <Button
                variant="contained"
                onClick={toggleAudio}
                size="small"
                sx={{ minWidth: "40px", padding: "6px" }}
                disabled={!audioSrc}
              >
                {isAudioPlaying ? <StopIcon /> : <VolumeUpIcon />}
              </Button>
              <Button variant="contained" onClick={captureFrame} size="small" sx={{ minWidth: "40px", padding: "6px" }}>
                <CameraAltIcon />
              </Button>
              <CopyIframeUrlButton />
              {hasMultipleVideos && (
                <>
                  <IconButton onClick={handleMenuClick}><MoreVertIcon /></IconButton>
                  <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
                    <MenuItem onClick={() => handleLayoutChange('side-by-side')}>Side-by-Side</MenuItem>
                    <MenuItem onClick={() => handleLayoutChange('vertical')}>Vertical</MenuItem>
                    <MenuItem onClick={() => handleLayoutChange('left')}>Left Only</MenuItem>
                    <MenuItem onClick={() => handleLayoutChange('right')}>Right Only</MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default VideoFrameScrubber;